/**
 * Вывод лапок: кольцо, Telegram Premium и заявки от 3000 лапок.
 */

import { useEffect, useMemo, useState } from "react";

import {
  formatPaws,
  parsePaws,
  type WithdrawalKind,
  type WithdrawalOptionDto,
  type WithdrawalRequestDto,
} from "@koshkodrop/shared";

import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "../store/session";

interface Props {
  onBack: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "В обработке",
  completed: "Выполнено",
  refunded: "Возврат",
  failed: "Ошибка",
};

export function WithdrawPage({ onBack }: Props) {
  const balance = useSession((state) => state.balance);
  const setBalance = useSession((state) => state.setBalance);
  const refreshProfile = useSession((state) => state.refreshProfile);

  const [options, setOptions] = useState<WithdrawalOptionDto[]>([]);
  const [requests, setRequests] = useState<WithdrawalRequestDto[]>([]);
  const [selected, setSelected] = useState<WithdrawalOptionDto | null>(null);
  const [contactUsername, setContactUsername] = useState("");
  const [customAmount, setCustomAmount] = useState("3000");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [optionsData, requestsData] = await Promise.all([
          api.withdrawalOptions(),
          api.withdrawals(),
        ]);
        setOptions(optionsData.options);
        setRequests(requestsData.requests);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : "Не удалось загрузить выводы");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const customAmountPaws = useMemo(() => {
    try {
      return parsePaws(customAmount.replace(",", ".").trim() || "0");
    } catch {
      return null;
    }
  }, [customAmount]);

  const submit = async () => {
    if (!selected || busy || !canSubmit) return;

    if (selectedAmountPaws && balance < selectedAmountPaws) {
      haptic.error();
      setError("Недостаточно лапок на балансе");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.createWithdrawal({
        kind: selected.kind,
        amountPaws:
          selected.kind === "custom" && customAmountPaws
            ? customAmountPaws.toString()
            : undefined,
        contactUsername: selected.kind === "custom" ? contactUsername : undefined,
      });

      setRequests((prev) => [result.request, ...prev]);
      setBalance(BigInt(result.balance));
      await refreshProfile();

      if (result.request.status === "completed") {
        setSuccess("Готово! Подарок уже у тебя в Telegram 🎉");
        haptic.success();
      } else if (selected.kind === "custom") {
        setSuccess("Заявка отправлена — свяжемся с тобой в Telegram");
        haptic.success();
      } else {
        setSuccess("Заявка создана, подарок скоро придёт");
        haptic.success();
      }

      setSelected(null);
      setContactUsername("");
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось оформить вывод");
    } finally {
      setBusy(false);
    }
  };

  const requiredAmount = (option: WithdrawalOptionDto): bigint => {
    if (option.kind === "custom") {
      if (customAmountPaws && customAmountPaws >= BigInt(option.minAmountPaws)) {
        return customAmountPaws;
      }
      return BigInt(option.minAmountPaws);
    }
    return BigInt(option.amountPaws);
  };

  const canAfford = (option: WithdrawalOptionDto) => balance >= requiredAmount(option);

  const selectedAmountPaws =
    selected?.kind === "custom"
      ? customAmountPaws
      : selected
        ? BigInt(selected.amountPaws)
        : null;

  const canSubmit =
    Boolean(selected) &&
    selectedAmountPaws !== null &&
    selectedAmountPaws > 0n &&
    balance >= selectedAmountPaws &&
    (selected?.kind !== "custom" ||
      (contactUsername.trim().length > 0 &&
        selectedAmountPaws >= BigInt(selected.minAmountPaws)));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl bg-night-850 px-3 py-2 text-sm font-bold text-white/60"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-extrabold text-white">Вывод</h1>
      </div>

      <section className="rounded-3xl border border-paws-500/20 bg-gradient-to-br from-paws-500/15 via-night-850 to-night-900 p-4 text-center">
        <p className="text-[11px] font-bold uppercase tracking-widest text-paws-400">Доступно</p>
        <p className="tabular mt-1 text-3xl font-extrabold text-white">
          {formatPaws(balance, { decimals: 2 })}
          <span className="ml-2 text-xl">🐾</span>
        </p>
      </section>

      {error && (
        <p className="rounded-2xl border border-lose-500/30 bg-lose-500/10 px-4 py-3 text-sm text-lose-300">
          {error}
        </p>
      )}
      {success && (
        <p className="rounded-2xl border border-win-500/30 bg-win-500/10 px-4 py-3 text-sm text-win-300">
          {success}
        </p>
      )}

      {loading ? (
        <p className="text-center text-sm text-white/40">Загружаю…</p>
      ) : (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Обменять лапки
          </h2>
          <div className="grid gap-2">
            {options.map((option) => {
              const affordable = canAfford(option);
              const active = selected?.kind === option.kind;

              return (
                <button
                  key={option.kind}
                  type="button"
                  disabled={!affordable}
                  onClick={() => {
                    if (!affordable) return;
                    haptic.select();
                    setSelected(option);
                    setError(null);
                    setSuccess(null);
                  }}
                  className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition ${
                    active
                      ? "border-paws-500/60 bg-paws-500/15"
                      : affordable
                        ? "border-white/8 bg-night-850/70"
                        : "border-white/5 bg-night-900/50 opacity-45"
                  }`}
                >
                  <span className="text-3xl">{option.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-extrabold text-white">{option.title}</p>
                    <p className="text-xs text-white/45">{option.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="tabular text-sm font-bold text-paws-300">
                      {option.kind === "custom"
                        ? `от ${formatPaws(BigInt(option.minAmountPaws))}`
                        : formatPaws(BigInt(option.amountPaws))}
                    </p>
                    {option.instant && (
                      <p className="text-[10px] font-bold uppercase text-win-400">мгновенно</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {selected?.kind === "custom" && (
        <section className="space-y-3 rounded-3xl border border-white/5 bg-night-850/70 p-4">
          <p className="text-sm font-semibold text-white/70">Заявка на вывод</p>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase text-white/30">Сумма лапок</label>
            <div className="flex items-center gap-2 rounded-2xl bg-night-900 px-3 py-2.5">
              <input
                value={customAmount}
                onChange={(event) =>
                  setCustomAmount(event.target.value.replace(/[^\d.,]/g, ""))
                }
                inputMode="decimal"
                className="tabular w-full bg-transparent text-lg font-bold text-white outline-none"
              />
              <span className="text-sm text-paws-400">🐾</span>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase text-white/30">
              @username для связи
            </label>
            <input
              value={contactUsername}
              onChange={(event) => setContactUsername(event.target.value)}
              placeholder="@твой_юзер"
              className="w-full rounded-2xl bg-night-900 px-3 py-2.5 text-sm text-white outline-none"
            />
          </div>
        </section>
      )}

      {selected && !canAfford(selected) && (
        <p className="text-center text-sm font-semibold text-lose-400">
          Недостаточно лапок — доступно {formatPaws(balance)}
        </p>
      )}

      {selected && (
        <button
          type="button"
          disabled={busy || !canSubmit}
          onClick={() => void submit()}
          className="w-full rounded-2xl bg-paws-500 py-4 text-base font-extrabold text-night-950 disabled:opacity-35"
        >
          {busy
            ? "Оформляю…"
            : selected.kind === "custom"
              ? "Оставить заявку"
              : `Обменять за ${formatPaws(BigInt(selected.amountPaws))}`}
        </button>
      )}

      {requests.length > 0 && (
        <section className="rounded-3xl border border-white/5 bg-night-850/60">
          <h3 className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white/40">
            Мои заявки
          </h3>
          <ul className="divide-y divide-white/5">
            {requests.map((item) => (
              <li key={item.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-white/80">
                    {kindLabel(item.kind)}
                  </p>
                  <span className="text-xs font-bold text-white/45">
                    {STATUS_LABELS[item.status] ?? item.status}
                  </span>
                </div>
                <p className="tabular mt-0.5 text-xs text-paws-300">
                  {formatPaws(BigInt(item.amountPaws))}
                  {item.holdActive && item.status === "pending" ? " · в холде" : ""}
                </p>
                {item.contactUsername && (
                  <p className="mt-0.5 text-[11px] text-white/35">@{item.contactUsername}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function kindLabel(kind: WithdrawalKind): string {
  switch (kind) {
    case "ring":
      return "Кольцо 💍";
    case "trophy":
      return "Кубок 🏆";
    case "diamond":
      return "Алмаз 💎";
    case "premium_3":
      return "Premium 3 мес.";
    case "premium_6":
      return "Premium 6 мес.";
    case "premium_12":
      return "Premium 12 мес.";
    case "custom":
      return "Заявка на вывод";
    default:
      return kind;
  }
}
